import {
  CheckIcon,
  ChevronRightIcon,
  MinusIcon
} from '@heroicons/react/24/outline';
import type { ColumnType } from 'antd/es/table';
import cn from 'classnames';
import type { MouseEvent } from 'react';

import { sortByTime } from 'utils/antd/sorters';
import { formatDate } from 'utils/dayjs';

import type { Webhook } from 'api';

import { ColumnTitle } from '@atoms/ColumnTitle';
import { TableSortIcon } from '@atoms/Icon/TableSortIcon';

import {
  WEBHOOKS_CHEVRON_TEST_ID,
  WEBHOOKS_SELECT_ALL_TEST_ID,
  WEBHOOKS_SELECT_ROW_TEST_ID,
  WEBHOOKS_STATUS_BADGE_TEST_ID
} from './constants';
import { webhookFormatLabel } from './utils';
import { WebhookStatusBadge } from './WebhookStatusBadge';
import { WebhooksUuidCell } from './WebhooksUuidCell';

const monoCellClass = 'font-mono text-content-primary';
const textCellClass = 'text-content-primary';
const EMPTY_CELL = '—';

const sortableHeader = {
  sortIcon: ({ sortOrder }: { sortOrder?: 'ascend' | 'descend' | null }) => (
    <TableSortIcon sortOrder={sortOrder} />
  ),
  onHeaderCell: () => ({ className: 'align-sorter-icon' })
};

type WebhookColumnsParams = {
  canManage: boolean;
  selectedUuids: Set<string>;
  allSelected: boolean;
  someSelected: boolean;
  onToggleRow: (uuid: string) => void;
  onToggleAll: () => void;
};

type WebhookCheckboxProps = {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  'data-testid': string;
  'aria-label': string;
};

const WebhookCheckbox = ({
  checked,
  indeterminate = false,
  onChange,
  'data-testid': testId,
  'aria-label': ariaLabel
}: WebhookCheckboxProps) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={indeterminate ? 'mixed' : checked}
    aria-label={ariaLabel}
    data-testid={testId}
    onClick={onChange}
    className={cn(
      'flex size-5 cursor-pointer items-center justify-center rounded border',
      checked || indeterminate
        ? 'border-transparent bg-action-secondary'
        : 'border-content-subtle bg-white'
    )}
  >
    {indeterminate ? (
      <MinusIcon className="size-3.5 stroke-[3] text-content-secondary" />
    ) : checked ? (
      <CheckIcon className="size-3.5 stroke-[3] text-white" />
    ) : null}
  </button>
);

export const getColumns = ({
  canManage,
  selectedUuids,
  allSelected,
  someSelected,
  onToggleRow,
  onToggleAll
}: WebhookColumnsParams): ColumnType<Webhook>[] => {
  const selectColumn: ColumnType<Webhook> = {
    key: 'select',
    width: 52,
    align: 'center',
    onCell: () => ({
      onClick: (e: MouseEvent) => e.stopPropagation()
    }),
    title: (
      <div className="flex justify-center">
        <WebhookCheckbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={onToggleAll}
          data-testid={WEBHOOKS_SELECT_ALL_TEST_ID}
          aria-label="Select all webhooks"
        />
      </div>
    ),
    render: (_value, record: Webhook) =>
      record.editable ? (
        <div className="flex justify-center">
          <WebhookCheckbox
            checked={selectedUuids.has(record.uuid)}
            onChange={() => onToggleRow(record.uuid)}
            data-testid={WEBHOOKS_SELECT_ROW_TEST_ID(record.uuid)}
            aria-label={`Select webhook ${record.uuid}`}
          />
        </div>
      ) : null
  };

  return [
    ...(canManage ? [selectColumn] : []),
    {
      title: <ColumnTitle title="Endpoint URL" />,
      dataIndex: 'destination_url',
      key: 'destination_url',
      width: 340,
      render: (destinationUrl?: string | null) =>
        destinationUrl ? (
          <span className={`${monoCellClass} break-all`}>{destinationUrl}</span>
        ) : (
          <span className={textCellClass}>{EMPTY_CELL}</span>
        )
    },
    {
      title: <ColumnTitle title="Business unit" />,
      dataIndex: 'business_unit_name',
      key: 'business_unit_name',
      width: 150,
      render: (value: string) => (
        <span className={cn(textCellClass, 'font-medium')}>{value}</span>
      )
    },
    {
      title: <ColumnTitle title="X-Auth-UUID" />,
      dataIndex: 'auth_uuid',
      key: 'auth_uuid',
      width: 250,
      render: (authUuid?: string | null) =>
        authUuid ? (
          <WebhooksUuidCell uuid={authUuid} />
        ) : (
          <span className={textCellClass}>{EMPTY_CELL}</span>
        )
    },
    {
      title: <ColumnTitle title="Event" />,
      dataIndex: 'event',
      key: 'event',
      width: 250,
      render: (event: string) => <span className={monoCellClass}>{event}</span>
    },
    {
      title: <ColumnTitle title="Format" />,
      dataIndex: 'format',
      key: 'format',
      width: 110,
      render: (format: string) => (
        <span className={textCellClass}>{webhookFormatLabel(format)}</span>
      )
    },
    {
      ...sortableHeader,
      title: <ColumnTitle title="Date created" />,
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      sorter: (a, b) => sortByTime(a.created_at, b.created_at),
      defaultSortOrder: 'descend',
      render: (createdAt: string) => (
        <span className={textCellClass}>{formatDate(createdAt)}</span>
      )
    },
    {
      title: <ColumnTitle title="Status" />,
      dataIndex: 'active',
      key: 'status',
      width: 110,
      align: 'right',
      render: (_value, record: Webhook) => (
        <div className="flex justify-end">
          <span data-testid={WEBHOOKS_STATUS_BADGE_TEST_ID(record.uuid)}>
            <WebhookStatusBadge active={record.active} />
          </span>
        </div>
      )
    },
    {
      dataIndex: 'chevron',
      key: 'chevron',
      width: 48,
      align: 'center',
      render: (_value, record: Webhook) => (
        <div className="flex justify-center">
          <ChevronRightIcon
            data-testid={WEBHOOKS_CHEVRON_TEST_ID(record.uuid)}
            className="h-6 stroke-content-subtle group-hover:stroke-content-primary"
          />
        </div>
      )
    }
  ];
};
